import { useTranslation } from "@refinedev/core";
import { CollapsibleSection } from "components/collapsibleSection";
import Input from "components/input";
import Select from "components/select";
import { Tag } from "components/tag";
import {
  IPickupSportParticipantDetail,
  IPickupSportParticipantForm,
} from "interfaces";
import moment from "moment";
import { Controller, useFormContext } from "react-hook-form";
import { SELF_PICK_STATUS } from "utills/constants";

export const EditPickupInformationSection = ({
  participantDetailData,
}: {
  participantDetailData: IPickupSportParticipantDetail | null;
}) => {
  const { translate } = useTranslation();

  const {
    register,
    control,
    formState: { errors },
  } = useFormContext<IPickupSportParticipantForm>(); // retrieve all hook methods

  return (
    <CollapsibleSection
      label={translate("pickup-information")}
      defaultOpen={true}
    >
      <div
        className="grid gap-x-4 gap-y-6 px-4 py-4 text-sm sm:px-6 grid-cols-12 lg:gap-x-6"
        style={{ wordBreak: "break-word" }}
      >
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-1">
          <div className="col-span-2">
            <p className="font-medium">Pickup email</p>
          </div>
          <div className="col-span-4">
            <Input
              id="email_pick"
              type="text"
              placeholder="Pickup email"
              error={errors["email_pick"]}
              {...register("email_pick")}
            />
            {errors["email_pick"] ? (
              <p className="error-text">
                <span className="font-medium">
                  {errors?.email_pick.message}
                </span>
              </p>
            ) : null}
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-3">
          <div className="col-span-2">
            <p className="font-medium">{translate("pickup-name")}</p>
          </div>
          <div className="col-span-4">
            <Input
              id="name_pick"
              type="text"
              placeholder={translate("pickup-name")}
              error={errors["name_pick"]}
              {...register("name_pick")}
            />
            {errors["name_pick"] ? (
              <p className="error-text">
                <span className="font-medium">{errors?.name_pick.message}</span>
              </p>
            ) : null}
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-5">
          <div className="col-span-2">
            <p className="font-medium">{translate("pos-name")}</p>
          </div>
          <div className="col-span-4">
            <p>{participantDetailData?.participant.pos}</p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-7">
          <div className="col-span-2">
            <p className="font-medium">{translate("pickup-at")}</p>
          </div>
          <div className="col-span-4">
            <p>
              {participantDetailData?.participant.pick_time
                ? moment(participantDetailData.participant.pick_time).format(
                    "DD/MM/YYYY HH:mm:ss",
                  )
                : ""}
            </p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-9">
          <div className="col-span-2">
            <p className="font-medium">{translate("pickup-image")}</p>
          </div>
          <div className="col-span-4">
            <p>
              {participantDetailData?.participant.image_pick ? (
                <img
                  className="w-auto max-w-full max-h-20"
                  src={participantDetailData?.participant.image_pick}
                  alt="PickupImage"
                />
              ) : (
                ""
              )}
            </p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-2">
          <div className="col-span-2">
            <p className="font-medium">{translate("pickup-id")}</p>
          </div>
          <div className="col-span-4">
            <Input
              id="id_card_pick"
              type="text"
              placeholder={translate("pickup-id")}
              error={errors["id_card_pick"]}
              {...register("id_card_pick")}
            />
            {errors["id_card_pick"] ? (
              <p className="error-text">
                <span className="font-medium">
                  {errors?.id_card_pick.message}
                </span>
              </p>
            ) : null}
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-4">
          <div className="col-span-2">
            <p className="font-medium">{translate("pickup-phone")}</p>
          </div>
          <div className="col-span-4">
            <Input
              id="phone_pick"
              type="text"
              placeholder={translate("pickup-phone")}
              error={errors["phone_pick"]}
              {...register("phone_pick")}
            />
            {errors["phone_pick"] ? (
              <p className="error-text">
                <span className="font-medium">
                  {errors?.phone_pick.message}
                </span>
              </p>
            ) : null}
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-6">
          <div className="col-span-2">
            <p className="font-medium">{translate("ewaiver-status")}</p>
          </div>
          <div className="col-span-4">
            <Tag
              text={
                participantDetailData?.participant?.is_online_checkin
                  ? "Signed"
                  : "Unsigned"
              }
              type={
                participantDetailData?.participant?.is_online_checkin
                  ? "green"
                  : "pink"
              }
            />
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-8">
          <div className="col-span-2">
            <p className="font-medium">{translate("pickup-method")}</p>
          </div>
          <div className="col-span-4">
            <Controller
              control={control}
              name="self_pick"
              render={({ field: { onChange, value = "", ref } }) => (
                <Select
                  key="self_pick"
                  inputRef={ref}
                  options={SELF_PICK_STATUS}
                  id="self_pick"
                  placeholder={translate("select-status")}
                  defaultValue={
                    SELF_PICK_STATUS.find(
                      (selected: { label: string; value: boolean }) =>
                        selected.value === value,
                    ) ?? ""
                  }
                  value={SELF_PICK_STATUS.find(
                    (selected: { label: string; value: boolean }) =>
                      selected.value === value,
                  )}
                  onChange={(selected: { label: string; value: string }) => {
                    onChange(selected?.value);
                  }}
                  error={errors["self_pick"]}
                />
              )}
            />
            {errors["self_pick"] ? (
              <p className="error-text">
                <span className="font-medium">
                  {errors["self_pick"]?.message}
                </span>
              </p>
            ) : null}
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-10">
          <div className="col-span-2">
            <p className="font-medium">Digital-Signature</p>
          </div>
          <div className="col-span-4">
            {participantDetailData?.participant.signature ? (
              <img
                className="w-auto max-w-full max-h-20"
                src={participantDetailData?.participant.signature}
                alt="signature"
              />
            ) : (
              ""
            )}
          </div>
        </div>
      </div>
    </CollapsibleSection>
  );
};
